/**
 * Improved Quick Add to Cart with Minmaxify Integration
 * This script fixes the quick add buttons to respect minimum order quantities.
 */

(function() {
  // Configuration
  const DEBUG = true; // Enable logging for troubleshooting
  const DEFAULT_MIN_QTY = 12; // Fallback default minimum quantity
  
  // Debug logging function
  function log(message, data) {
    if (DEBUG) {
      console.log(`[QuickAdd Fix] ${message}`, data || '');
    }
  }
  
  // Wait for DOM to be fully loaded
  document.addEventListener('DOMContentLoaded', function() {
    log('Initializing quick add fix');
    
    // We need to override the addToCart function from simple-quick-add.js
    // Rather than modify that file, we'll define our own function and
    // replace it when buttons are created
    
    // Wait a short time for other scripts to initialize
    setTimeout(fixQuickAddButtons, 1000);
    
    // Also set up an observer to catch any dynamically added buttons
    observeDynamicButtons();
  });
  
  // Main function to fix quick add buttons
  function fixQuickAddButtons() {
    // Target all quick add buttons, including those created by simple-quick-add.js
    const quickAddButtons = document.querySelectorAll(
      '.quick-add-btn:not([data-min-qty-fixed]), ' +
      'button.add-to-cart:not([data-min-qty-fixed]), ' +
      'button.add_to_cart:not([data-min-qty-fixed])'
    );
    
    log(`Found ${quickAddButtons.length} quick add buttons to fix`);
    
    quickAddButtons.forEach(button => {
      // Skip if already fixed
      if (button.hasAttribute('data-min-qty-fixed')) {
        return;
      }
      
      // Mark as fixed
      button.setAttribute('data-min-qty-fixed', 'true');
      
      // Get product info
      const productId = button.getAttribute('data-variant-id') || 
                         button.getAttribute('data-product-id');
      
      const productUrl = button.getAttribute('data-product-url');
      
      if (!productId && !productUrl) {
        log('Button missing product info, skipping', button);
        return;
      }
      
      // Save original click handler
      const originalOnClick = button.onclick;
      
      // Replace with our handler
      button.onclick = function(event) {
        event.preventDefault();
        event.stopPropagation();
        
        // Set button to loading state
        const originalText = button.textContent;
        button.textContent = 'Loading...';
        button.disabled = true;
        
        // If we already have product ID, add to cart with minimum quantity
        if (productId) {
          getMinimumQuantity(productId)
            .then(minQty => {
              log(`Adding ${productId} with quantity ${minQty}`);
              return addToCartWithMinQty(productId, minQty);
            })
            .then(() => {
              button.textContent = 'Added!';
              setTimeout(() => {
                button.textContent = originalText;
                button.disabled = false;
              }, 2000);
            })
            .catch(error => {
              log('Error adding to cart', error);
              button.textContent = 'Error';
              setTimeout(() => {
                button.textContent = originalText;
                button.disabled = false;
              }, 2000);
            });
        }
        // Otherwise fetch product data first
        else if (productUrl) {
          fetchProductData(productUrl)
            .then(product => {
              if (!product || !product.variants || product.variants.length === 0) {
                throw new Error('Invalid product data');
              }
              
              const variantId = product.variants[0].id;
              
              return getMinimumQuantity(variantId)
                .then(minQty => {
                  log(`Adding ${variantId} with quantity ${minQty}`);
                  return addToCartWithMinQty(variantId, minQty);
                });
            })
            .then(() => {
              button.textContent = 'Added!';
              setTimeout(() => {
                button.textContent = originalText;
                button.disabled = false;
              }, 2000);
            })
            .catch(error => {
              log('Error adding to cart', error);
              button.textContent = 'Error';
              setTimeout(() => {
                button.textContent = originalText;
                button.disabled = false;
              }, 2000);
            });
        }
        
        return false;
      };
    });
  }
  
  // Fetch product data
  function fetchProductData(productUrl) {
    // Clean the URL
    let jsonUrl = productUrl;
    if (jsonUrl.indexOf('?') > -1) {
      jsonUrl = jsonUrl.split('?')[0];
    }
    if (!jsonUrl.endsWith('.js')) {
      jsonUrl = jsonUrl + '.js';
    }
    
    return fetch(jsonUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Error fetching product: ${response.statusText}`);
        }
        return response.json();
      });
  }
  
  // Get minimum quantity for a product
  function getMinimumQuantity(productId) {
    // Here we'll try several methods to determine the minimum quantity
    
    // First check if Minmaxify has a global configuration
    if (window.Minmaxify && window.Minmaxify.products) {
      const product = window.Minmaxify.products[productId];
      if (product && product.minimum) {
        log(`Found minimum quantity ${product.minimum} from Minmaxify global`);
        return Promise.resolve(parseInt(product.minimum, 10));
      }
    }
    
    // Check for data attributes on product elements
    const productElement = document.querySelector(`[data-product-id="${productId}"], [data-variant-id="${productId}"]`);
    if (productElement) {
      const minQtyAttrs = ['data-minimum-quantity', 'data-min-qty', 'data-minmaxify-min'];
      for (const attr of minQtyAttrs) {
        if (productElement.hasAttribute(attr)) {
          const minQty = parseInt(productElement.getAttribute(attr), 10);
          if (!isNaN(minQty) && minQty > 0) {
            log(`Found minimum quantity ${minQty} from data attribute`);
            return Promise.resolve(minQty);
          }
        }
      }
    }
    
    // Check error messages on the page
    const errorElements = document.querySelectorAll('.errors, .error-message, .cart-error');
    for (const errorEl of errorElements) {
      const text = errorEl.textContent || '';
      const match = text.match(/must have at least (\d+) of this item/i);
      if (match && match[1]) {
        const minQty = parseInt(match[1], 10);
        if (!isNaN(minQty) && minQty > 0) {
          log(`Found minimum quantity ${minQty} from error message`);
          return Promise.resolve(minQty);
        }
      }
    }
    
    // Finally, use the default
    log(`Using default minimum quantity ${DEFAULT_MIN_QTY}`);
    return Promise.resolve(DEFAULT_MIN_QTY);
  }
  
  // Add to cart with minimum quantity
  function addToCartWithMinQty(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.description || 'Error adding to cart');
        });
      }
      return response.json();
    })
    .then(data => {
      log('Successfully added to cart', data);
      
      // Show notification
      showNotification(`Added to cart (Qty: ${quantity})`);
      
      // Update cart
      if (typeof timber !== 'undefined' && timber.RightDrawer) {
        timber.RightDrawer.open();
      }
      
      // Update cart count
      updateCartCount();
      
      return data;
    });
  }
  
  // Update cart count display
  function updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update cart count elements
        const cartCountElements = document.querySelectorAll('.cart-count, .cart-items-count, .cart-link__bubble');
        cartCountElements.forEach(element => {
          if (element.classList.contains('cart-link__bubble')) {
            if (cart.item_count > 0) {
              element.classList.add('cart-link__bubble--visible');
            }
          } else {
            element.textContent = cart.item_count;
          }
        });
        
        // Refresh Ajax cart if applicable
        if (typeof ajaxCart !== 'undefined' && typeof ajaxCart.load === 'function') {
          ajaxCart.load();
        }
      })
      .catch(error => {
        log('Error updating cart count', error);
      });
  }
  
  // Show notification message
  function showNotification(message, type = 'success') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `quick-add-notification quick-add-notification--${type}`;
    notification.textContent = message;
    
    // Style it
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: '9999',
      padding: '12px 20px',
      backgroundColor: type === 'success' ? '#4CAF50' : '#F44336',
      color: 'white',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      fontSize: '14px'
    });
    
    // Add to page
    document.body.appendChild(notification);
    
    // Remove after delay
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 3000);
  }
  
  // Observe DOM for dynamically added buttons
  function observeDynamicButtons() {
    const observer = new MutationObserver(mutations => {
      let hasNewButtons = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              // Check if this node is a button or contains buttons
              if (
                (node.classList && 
                  (node.classList.contains('quick-add-btn') || 
                   node.classList.contains('add-to-cart') || 
                   node.classList.contains('add_to_cart'))) || 
                node.querySelector('.quick-add-btn, .add-to-cart, .add_to_cart')
              ) {
                hasNewButtons = true;
              }
            }
          });
        }
      });
      
      if (hasNewButtons) {
        log('Detected new buttons added to the DOM');
        setTimeout(fixQuickAddButtons, 100);
      }
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    log('Started observing for dynamic buttons');
  }
})();